import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Zap, CalendarClock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";

export default function RentsPage({ readOnly = false }) {
  const [rents, setRents] = useState([]);
  const [leases, setLeases] = useState([]);
  const [open, setOpen] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ lease_id: "", amount: 0, period_month: "", paid_date: "", status: "paid", method: "", notes: "" });

  const load = async () => {
    const r = await api.get("/rents"); setRents(r.data);
    if (!readOnly) {
      const [l, s] = await Promise.all([api.get("/leases"), api.get("/rents/auto-invoicing/status").catch(() => ({ data: null }))]);
      setLeases(l.data);
      setAutoStatus(s.data);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rents", { ...form, amount: Number(form.amount) });
      toast.success("Recorded");
      setOpen(false); setForm({ lease_id: "", amount: 0, period_month: "", paid_date: "", status: "paid", method: "", notes: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete record?")) return;
    await api.delete(`/rents/${id}`); load();
  };

  const onLeaseChange = (id) => {
    const l = leases.find(x => x.id === id);
    setForm({ ...form, lease_id: id, amount: l?.monthly_rent || 0 });
  };

  const markPaid = async (rent) => {
    // Quick action: clone as paid version (we don't have PATCH, so delete then re-insert)
    try {
      await api.delete(`/rents/${rent.id}`);
      await api.post("/rents", {
        lease_id: rent.lease_id, amount: rent.amount, period_month: rent.period_month,
        paid_date: new Date().toISOString().slice(0, 10), status: "paid",
        method: rent.method || "", notes: rent.notes || ""
      });
      toast.success("Marked as paid");
      load();
    } catch (e) { toast.error("Could not update"); }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/rents/generate-month");
      toast.success(`Generated ${data.created} new invoice${data.created !== 1 ? "s" : ""}${data.skipped_existing ? ` · ${data.skipped_existing} already existed` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setGenerating(false);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/rents/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rojasproperty-rents-${today}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rents.length} record${rents.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <AppLayout
      title={readOnly ? "Rent History" : "Rent Records"}
      subtitle="Monthly rent payments — paid, unpaid, partial"
      actions={!readOnly && (
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/rents/export.csv"
            filenameBase="rojasproperty-rents"
            testidPrefix="export-rents"
            disabled={rents.length === 0}
          />
          <Button data-testid="generate-month-button" onClick={generateNow} disabled={generating} variant="outline" className="gap-2">
            <Sparkles className="h-4 w-4" /> {generating ? "Generating…" : "Generate This Month"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-rent-button" className="bg-slate-900 hover:bg-slate-800 gap-2"><Plus className="h-4 w-4" /> Record Rent</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record a rent payment</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Lease</Label>
                  <Select value={form.lease_id} onValueChange={onLeaseChange}>
                    <SelectTrigger data-testid="rent-lease-select"><SelectValue placeholder="Select lease" /></SelectTrigger>
                    <SelectContent>{leases.map(l => <SelectItem key={l.id} value={l.id}>{l.property?.name} — {l.tenant?.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Period (YYYY-MM)</Label><Input data-testid="rent-period" required placeholder="2026-02" pattern="\d{4}-\d{2}" value={form.period_month} onChange={e => setForm({ ...form, period_month: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Amount ($)</Label><Input type="number" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="rent-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Paid date</Label><Input type="date" value={form.paid_date} onChange={e => setForm({ ...form, paid_date: e.target.value })} /></div>
                  <div className="space-y-1.5 col-span-2"><Label className="text-xs">Method (cash, check, transfer…)</Label><Input value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} /></div>
                  <div className="space-y-1.5 col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="rent-submit" type="submit" className="bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}
    >
      {!readOnly && autoStatus && (
        <Card className="p-5 mb-6 border-slate-200 bg-gradient-to-br from-white to-slate-50" data-testid="auto-invoicing-banner">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center shrink-0">
              <Zap className="h-5 w-5 text-white" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm">Auto-invoicing is on</div>
              <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <CalendarClock className="h-3.5 w-3.5" />
                Next run: <span className="font-mono-num">{autoStatus.next_period}-01</span> · {autoStatus.schedule}
                {autoStatus.last_run && (
                  <span className="ml-2 text-slate-400">
                    · Last: {autoStatus.last_run.period_month} ({autoStatus.last_run.result?.created ?? 0} created, {autoStatus.last_run.trigger})
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">{autoStatus.current_period} invoices</div>
              <div className="font-display text-2xl font-semibold font-mono-num">{autoStatus.current_period_invoices}</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead><TableHead>Property</TableHead><TableHead>Tenant</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Source</TableHead>{!readOnly && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rents.length === 0 ? (
              <TableRow><TableCell colSpan={readOnly ? 6 : 7} className="text-center text-slate-400 py-8">No rent records yet</TableCell></TableRow>
            ) : rents.map(r => (
              <TableRow key={r.id} data-testid={`rent-row-${r.id}`}>
                <TableCell className="font-mono-num">{r.period_month}</TableCell>
                <TableCell>{r.property_name}</TableCell>
                <TableCell>{r.tenant_name}</TableCell>
                <TableCell className="font-mono-num">${r.amount}</TableCell>
                <TableCell><StatusPill status={r.status} /></TableCell>
                <TableCell className="text-xs text-slate-500">
                  {r.auto_generated ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100"><Zap className="h-3 w-3" /> auto</span> : <span className="text-slate-400">manual</span>}
                </TableCell>
                {!readOnly && (
                  <TableCell className="text-right">
                    {r.status !== "paid" && (
                      <button onClick={() => markPaid(r)} data-testid={`mark-paid-${r.id}`} className="text-emerald-600 hover:text-emerald-700 text-xs font-medium mr-3">Mark paid</button>
                    )}
                    <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4 inline" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </AppLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-700",
    unpaid: "bg-rose-100 text-rose-700",
    partial: "bg-amber-100 text-amber-700",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${map[status] || "bg-slate-100 text-slate-700"}`}>{status}</span>;
}
